<template>
  <div class="available-role-input-container">
    <FormLabel :label="label" :isRequired="isRequired && !readOnly" />
    <div class="available-role-input-wrapper">
      <!-- readonlyモードではテキスト表示 -->
      <div v-if="readOnly" class="readonly-rows">
        <div v-if="displayItem" class="readonly-row">
          {{ displayItem }}
        </div>
        <div v-else class="readonly-text">-</div>
      </div>
      <!-- 通常モードでは複数行の入力 -->
      <div v-else class="input-rows" :class="{ 'has-error': errorMessage }">
        <div v-for="(item, index) in localItems" :key="index" class="input-row">
          <div class="row-fields">
            <!-- 開始日 -->
            <div class="field field-date">
              <DateInput
                label=""
                :value="item.start_date"
                placeholder="---- / -- / --"
                :disabled="disabled"
                @input="onStartDateChange(index, $event)"
              />
            </div>
            <div class="field field-date-separator">〜</div>
            <!-- 辞令職 -->
            <div class="field field-role">
              <treeselect
                :value="item.role_id"
                :options="roleOptions"
                :disabled="disabled"
                :multiple="false"
                :clearable="false"
                placeholder="辞令職"
                noOptionsText="なし"
                @input="onRoleChange(index, $event)"
              />
            </div>
            <!-- 執職可能役職 -->
            <div class="field field-available-role">
              <treeselect
                :value="item.available_role_id"
                :options="getAvailableRoleOptions(index)"
                :disabled="disabled || !item.role_id"
                :multiple="false"
                :clearable="false"
                placeholder="執職可能役職"
                noOptionsText="なし"
                @input="onAvailableRoleChange(index, $event)"
              />
            </div>
            <!-- 削除ボタン -->
            <div class="field field-delete" v-if="localItems.length > 1">
              <div
                class="flex justify-center items-center action-button-icon p-1"
                @click.stop="removeRow(index)"
              >
                <vs-icon icon="delete_outline" size="22px" />
              </div>
            </div>
          </div>
        </div>
        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
        <!-- 追加ボタン　-->
        <div class="add-button-wrapper flex justify-center">
          <TextButton
            type="border"
            colorType="secondary"
            label="+ 辞令職 / 執職可能役職を追加"
            :disabled="disabled"
            @click="addRow"
          />
        </div>
      </div>
    </div>

    <InputBottomDivider v-if="readOnly" />
  </div>
</template>

<script>
import FormLabel from "./FormLabel.vue";
import InputBottomDivider from "./InputBottomDivider.vue";
import DateInput from "./DateInput.vue";
import Button from "../Button.vue";
import TextButton from "../TextButton.vue";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";

export default {
  name: "AvailableRoleInput",
  components: {
    FormLabel,
    InputBottomDivider,
    DateInput,
    Button,
    TextButton,
    Treeselect,
  },
  props: {
    /**
     * ラベルテキスト
     * @type {String}
     * @required
     */
    label: {
      type: String,
      required: true,
    },
    /**
     * 入力値（配列）
     * @type {Array}
     * @default () => []
     * @example [{ start_date: "2024-01-01", role: 1, available_role: 2 }]
     */
    value: {
      type: Array,
      default: () => [],
    },
    /**
     * 役職オプション
     * @type {Array}
     * @default () => []
     * @example [{ id: 1, label: "船長", sort_order: 1, department: "甲板部" }, { id: 2, label: "機関長", sort_order: 2, department: "機関部" }]
     */
    roleOptions: {
      type: Array,
      default: () => [],
    },
    /**
     * 必須項目かどうか
     * @type {Boolean}
     * @default false
     */
    isRequired: {
      type: Boolean,
      default: false,
    },
    /**
     * 読み取り専用かどうか
     * @type {Boolean}
     * @default false
     */
    readOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * 無効化されているかどうか
     * @type {Boolean}
     * @default false
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * エラーメッセージ
     * @type {String}
     * @default ""
     */
    errorMessage: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      localItems: [],
    };
  },
  watch: {
    value: {
      handler(newValue) {
        // valueが空の場合は初期行を1つ作成
        if (!newValue || newValue.length === 0) {
          this.localItems = [
            {
              start_date: null,
              role_id: null,
              available_role_id: null,
            },
          ];
        } else {
          this.localItems = [...newValue];
        }
      },
      immediate: true,
      deep: true,
    },
  },
  computed: {
    displayItem() {
      if (!this.value || this.value.length === 0) {
        return null;
      }

      // start_dateが最新のitemを取得
      const itemsWithDate = this.value
        .filter(item => item.start_date)
        .map(item => ({
          ...item,
          dateValue: new Date(item.start_date),
        }))
        .filter(item => !isNaN(item.dateValue.getTime()));

      if (itemsWithDate.length === 0) {
        // start_dateがない場合は最初のitemを使用
        const firstItem = this.value[0];
        if (!firstItem) {
          return null;
        }
        return this.formatItem(firstItem);
      }

      // start_dateが最新のitemを選択
      const latestItem = itemsWithDate.reduce((latest, current) => {
        return current.dateValue > latest.dateValue ? current : latest;
      });

      return this.formatItem(latestItem);
    },
  },
  methods: {
    formatItem(item) {
      const datePart = [];
      const otherParts = [];

      if (item.start_date) {
        const date = new Date(item.start_date);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          datePart.push(`${year}/${month}/${day}〜`);
        }
      }
      if (item.role_id) {
        const roleOption = this.roleOptions.find(
          opt => opt.id === item.role_id || opt.id === String(item.role_id)
        );
        otherParts.push(roleOption ? roleOption.label : item.role_id);
      }
      if (item.available_role_id) {
        const availableRoleOption = this.roleOptions.find(
          opt =>
            opt.id === item.available_role_id ||
            opt.id === String(item.available_role_id)
        );
        otherParts.push(
          availableRoleOption
            ? availableRoleOption.label
            : item.available_role_id
        );
      }

      if (datePart.length === 0 && otherParts.length === 0) {
        return "-";
      }

      const dateStr = datePart.join("");
      const otherStr = otherParts.join(" / ");

      if (dateStr && otherStr) {
        return `${dateStr}　${otherStr}`;
      } else if (dateStr) {
        return dateStr;
      } else {
        return otherStr;
      }
    },
    onStartDateChange(index, dateValue) {
      const newItems = [...this.localItems];
      newItems[index] = {
        ...newItems[index],
        start_date: dateValue,
      };
      this.localItems = newItems;
      this.$emit("input", newItems);
    },
    onRoleChange(index, selectedRoleId) {
      const newItems = [...this.localItems];

      newItems[index] = {
        ...newItems[index],
        role_id: selectedRoleId || null,
        available_role_id: selectedRoleId || null,
      };
      this.localItems = newItems;
      this.$emit("input", newItems);
    },
    onAvailableRoleChange(index, selectedAvailableRoleId) {
      const newItems = [...this.localItems];
      newItems[index] = {
        ...newItems[index],
        available_role_id: selectedAvailableRoleId || null,
      };
      this.localItems = newItems;
      this.$emit("input", newItems);
    },
    addRow() {
      const newItems = [
        ...this.localItems,
        {
          start_date: null,
          role_id: null,
          available_role_id: null,
        },
      ];
      this.localItems = newItems;
      this.$emit("input", newItems);
    },
    removeRow(index) {
      if (this.localItems.length <= 1) {
        return;
      }
      const newItems = this.localItems.filter((_, i) => i !== index);
      this.localItems = newItems;
      this.$emit("input", newItems);
    },
    getAvailableRoleOptions(index) {
      const item = this.localItems[index];
      if (!item || !item.role_id) {
        return [];
      }

      // 選択されたroleを取得
      const selectedRole = this.roleOptions.find(
        opt => opt.id === item.role_id || opt.id === String(item.role_id)
      );

      if (!selectedRole) {
        return [];
      }

      const selectedDepartment = selectedRole.department;
      const selectedSortOrder = selectedRole.sort_order;

      // 同じdepartmentかつsort_orderがrole以下のものだけをフィルタリング
      return this.roleOptions.filter(option => {
        // departmentが一致するか確認
        if (option.department !== selectedDepartment) {
          return false;
        }

        // sort_orderがrole以下のもの
        if (option.sort_order == null || selectedSortOrder == null) {
          return false;
        }

        return option.sort_order <= selectedSortOrder;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/aisea/variables";

.available-role-input-container {
  display: flex;
  flex-direction: column;
  width: 100%;

  .available-role-input-wrapper {
    .readonly-rows {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      .readonly-row {
        padding: 0rem 0.75rem;
        font-family: $aisea-font-family;
        font-size: 16px;
        font-weight: 400;
        color: $aisea-dark-gray;
        line-height: 1.5;
        min-height: 26px;
      }

      .readonly-text {
        padding: 0rem 0.75rem;
        font-family: $aisea-font-family;
        font-size: 16px;
        font-weight: 400;
        color: $aisea-dark-gray;
        line-height: 1.5;
        min-height: 26px;
      }
    }

    .input-rows {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      .input-row {
        .row-fields {
          display: flex;
          gap: 0.5rem;
          align-items: center;

          .field {
            display: flex;
            flex-direction: column;

            &.field-date {
              width: 140px;
              flex-shrink: 0;
            }

            &.field-role {
              flex-shrink: 1;
            }

            &.field-available-role {
              flex-shrink: 1;
            }

            .action-button-icon {
              border-radius: 5px;
              cursor: pointer;
              transition: all 0.3s ease;
              &:hover {
                background: rgb(199, 199, 199);
              }
            }

            ::v-deep .vue-treeselect {
              .vue-treeselect__control {
                border: 1px solid $aisea-mouse-gray;
                border-radius: 6px;
                min-height: 36px;
                transition: border-color 0.3s ease, box-shadow 0.3s ease;
              }

              .vue-treeselect__control--focused {
                border-color: $aisea-blue;
                box-shadow: 0 0 0 2px rgba(45, 156, 219, 0.1);
              }

              &.vue-treeselect--disabled {
                .vue-treeselect__control {
                  background-color: $aisea-white;
                  border-color: $aisea-light-gray;
                  cursor: not-allowed;
                }
              }
            }
          }
        }
      }

      &.has-error {
        .input-row {
          .row-fields {
            .field {
              ::v-deep .vue-treeselect {
                .vue-treeselect__control {
                  border-color: $aisea-red;
                  background-color: rgba($aisea-red, 0.1);
                }
              }

              ::v-deep .date-input-container {
                .flat-pickr-wrapper {
                  input {
                    border-color: $aisea-red !important;
                    background-color: rgba($aisea-red, 0.1) !important;
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  .error-message {
    margin-top: 0.25rem;
    padding-left: 0.25rem;
    font-family: $aisea-font-family;
    font-size: 12px;
    font-weight: 400;
    color: $aisea-red;
    line-height: 1.5;
  }
}
</style>
